import {BaseQueryApi, FetchArgs} from '@reduxjs/toolkit/query';

import {wafProtectionService} from '@/services/wafProtection/WafProtectionService';

import {
  MAX_RETRIES,
  sessionExpiredResult,
  WAF_RETRY_DELAY_MS,
} from './constants';
import {
  handleHttpErrorResponse,
  handleWafResponse,
  logOut,
  normalizeHeaders,
  refreshUserSession,
} from './utils';

export const reAuth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: {},
  baseUrl: string,
  baseQuery: (arg0: any) => any,
) => {
  try {
    let retryCount = 1;

    if (typeof baseUrl !== 'string') {
      logOut(api);
      return sessionExpiredResult;
    }

    let result = await (await baseQuery(baseUrl))(args, api, extraOptions);

    while (retryCount <= MAX_RETRIES) {
      const errorStatus: number = result?.error?.status ?? 0;
      const metaResponse: Response | null = result?.meta?.response ?? null;
      const isWafChallenge = wafProtectionService.isChallenge(
        baseUrl,
        metaResponse,
      );

      if (isWafChallenge) {
        await new Promise(res => setTimeout(res, WAF_RETRY_DELAY_MS));
        result = await (await baseQuery(baseUrl))(args, api, extraOptions);
        retryCount++;
        continue;
      }

      switch (true) {
        // session expired, refresh session and retry
        case errorStatus === 401 && retryCount < MAX_RETRIES:
          result = await refreshUserSession({args, api, baseUrl, baseQuery});
          retryCount++;
          break;
        default:
          retryCount = MAX_RETRIES + 1;
          break;
      }

      if (metaResponse) {
        await handleWafResponse(baseUrl, metaResponse);
      }
    }

    return result ?? sessionExpiredResult;
  } catch (error: any) {
    if (typeof args !== 'string') {
      handleHttpErrorResponse(error, {
        url: args.url ?? '',
        method: args.method ?? '',
        body: args.body ?? null,
        headers: normalizeHeaders(args.headers),
      });
    }
    logOut(api);
    throw error;
  }
};
